import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_ORG_CONVERSATION_THREADS } from '../../../lib/graphql/queries/organizations';
import type { CrmStageProjection } from '../../../types/crmStage.types';

export interface OrgConversationThread {
  conversationId: string;
  contactId: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  channel: string;
  messageCount: number;
  unreadCount: number;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastDirection: string | null;
  sentiment: string | null;
  isStandalone: boolean;
  /** SPEC-1908: scroll target for the drill-down chat view.
   *  - With a date filter: newest message_id within the range
   *  - Without:            thread's most-recent message_id
   *  Use as the `anchor.messageId` when opening SmsThreadModal. */
  matchedMessageId: string | null;
  crmStages: CrmStageProjection[];
}

interface DateRange {
  from: Date | null;
  to: Date | null;
}

type Data = {
  organization?: {
    conversationThreads?: {
      items?: OrgConversationThread[];
      total?: number;
      hasMore?: boolean;
    };
  } | null;
};

const PAGE_SIZE = 25;

/**
 * Paginated org-wide conversation threads. For SMS (per SPEC-1899 in
 * PR #2448) the backend now groups by contact_id, so the FE gets one
 * row per contact instead of one per Twilio conversation_id.
 *
 * `dateRange` is forwarded to the resolver as dateFrom/dateTo. With a
 * range, the row's `lastMessageAt` / `lastMessagePreview` /
 * `lastDirection` / `matchedMessageId` all reflect the latest matching
 * message within the window.
 */
export const useConversationThreadsGQL = ({
  channel = 'sms',
  contactId,
  enabled = true,
  dateRange,
}: {
  channel?: string;
  contactId?: string;
  enabled?: boolean;
  dateRange?: DateRange;
}) => {
  const orgId = useOrganizationStore((s) => s.organization?.id) ?? '';
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { data, loading } = useQuery<Data>(GET_ORG_CONVERSATION_THREADS, {
    variables: {
      organizationId: orgId,
      channel,
      limit: currentPageSize,
      offset: currentPage * currentPageSize,
      contactId,
      search: search || undefined,
      dateFrom: dateRange?.from?.toISOString(),
      dateTo: dateRange?.to?.toISOString(),
    },
    skip: !enabled || !orgId,
    fetchPolicy: 'cache-and-network',
  });

  const items = data?.organization?.conversationThreads?.items ?? [];
  const total = data?.organization?.conversationThreads?.total ?? 0;
  const hasMore = data?.organization?.conversationThreads?.hasMore ?? false;

  const nextPage = useCallback(() => setCurrentPage((p) => p + 1), []);
  const prevPage = useCallback(() => setCurrentPage((p) => Math.max(0, p - 1)), []);
  const goToPage = useCallback((page: number) => setCurrentPage(Math.max(0, page)), []);
  const changePageSize = useCallback((size: number) => {
    setCurrentPageSize(size);
    setCurrentPage(0);
  }, []);
  const setSearchTerm = useCallback((value: string | undefined) => {
    setSearch(value);
    setCurrentPage(0);
  }, []);

  return {
    items,
    total,
    hasMore,
    isLoading: loading,
    currentPage,
    pageSize: currentPageSize,
    nextPage,
    prevPage,
    goToPage,
    changePageSize,
    setSearch: setSearchTerm,
    search,
  };
};
